import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import { renameMediaInputSchema, type MediaAsset } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";
import { MediaGrid } from "../components/MediaGrid";
import { UploadControl } from "../components/UploadControl";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Modal } from "../components/Modal";

export function MediaLibrary() {
  const api = useApi();
  const load = useCallback(() => api.listMedia(), [api]);
  const { data, loading, error, reload, setData } = useAsync<MediaAsset[]>(load, [load]);
  const [toRemove, setToRemove] = useState<MediaAsset | null>(null);
  const [toRename, setToRename] = useState<MediaAsset | null>(null);
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const noticeRef = useRef<HTMLParagraphElement>(null);
  const filenameRef = useRef<HTMLInputElement>(null);
  const renameFormId = useId();
  useEffect(() => { if (notice) noticeRef.current?.focus(); }, [notice]);
  useEffect(() => {
    if (!toRename) return;
    const frame = requestAnimationFrame(() => { filenameRef.current?.focus(); filenameRef.current?.select(); });
    return () => cancelAnimationFrame(frame);
  }, [toRename]);

  const cancelRename = () => {
    if (busy) return;
    setToRename(null); setFilename(""); setActionError(undefined);
  };
  const saveName = async (event: FormEvent) => {
    event.preventDefault();
    if (!toRename || busy) return;
    const result = renameMediaInputSchema.safeParse({ filename });
    if (!result.success) {
      setActionError(result.error.issues[0]?.message ?? "Enter a valid file name.");
      filenameRef.current?.focus(); return;
    }
    setBusy(true); setActionError(undefined);
    try {
      const updated = await api.renameMedia(toRename.id, result.data);
      setData(previous => previous?.map(asset => asset.id === updated.id ? updated : asset));
      setToRename(null); setFilename("");
      setNotice(`The file was renamed to “${updated.filename}”.`);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "Could not rename this file. Please try again.");
    } finally { setBusy(false); }
  };

  const confirmRemove = async () => {
    if (!toRemove || busy) return;
    setBusy(true);
    setActionError(undefined);
    try {
      await api.deleteMedia(toRemove.id);
      setData((previous) => previous?.filter((asset) => asset.id !== toRemove.id));
      setToRemove(null);
      setNotice(`“${toRemove.filename}” was removed.`);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "Could not remove this file. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Media Library</h1>
          <p className="page__subtitle">Images and files available to your content.</p>
        </div>
        <div className="page__actions">
          <UploadControl onUploaded={reload} />
        </div>
      </div>

      {loading ? <Spinner label="Loading media…" /> : null}
      {error ? <ErrorBanner message={error.message} onRetry={reload} /> : null}
      {notice ? <p ref={noticeRef} role="status" tabIndex={-1} className="media-removal-notice">{notice}</p> : null}
      {data ? <MediaGrid assets={data} showCopy actionBusy={busy} onRemove={(asset) => {
        setActionError(undefined); setNotice(undefined); setToRemove(asset);
      }} onRename={(asset) => {
        setActionError(undefined); setNotice(undefined); setFilename(asset.filename); setToRename(asset);
      }} /> : null}
      {toRemove ? <ConfirmDialog title="Remove media" confirmLabel="Remove file" danger busy={busy}
        message={`Remove “${toRemove.filename}” from the Media Library and public storage? Files used by saved or published content cannot be removed.`}
        error={actionError} onConfirm={() => void confirmRemove()}
        onCancel={() => { if (!busy) { setToRemove(null); setActionError(undefined); } }} /> : null}
      {toRename ? <Modal title="Rename media" onClose={cancelRename} dismissible={!busy} footer={<>
        <button type="button" className="btn btn--secondary" disabled={busy} onClick={cancelRename}>Cancel</button>
        <button type="submit" form={renameFormId} className="btn btn--primary" disabled={busy}>{busy ? "Saving…" : "Save name"}</button>
      </>}>
        <form id={renameFormId} onSubmit={event => void saveName(event)} aria-busy={busy}>
          <div className="form-field">
          <label className="form-field__label" htmlFor={`${renameFormId}-filename`}>File name</label>
          <input ref={filenameRef} id={`${renameFormId}-filename`} className="input" type="text"
            value={filename} maxLength={255} required disabled={busy}
            aria-describedby={`${renameFormId}-hint`}
            onChange={event => { setFilename(event.target.value); setActionError(undefined); }} />
          <p id={`${renameFormId}-hint`} className="form-hint">Changes the name shown in the library. Existing links keep working.</p>
          </div>
          {actionError ? <ErrorBanner message={actionError} /> : null}
        </form>
      </Modal> : null}
    </div>
  );
}
